import {
  deleteOperationalMessages,
  loadOperationalWorkspace,
  newOperationalRequestId,
  processOperationalMessage,
  uploadOperationalAttachments,
  type OperationalWorkspaceSnapshot,
} from "@/core/services/operational-ai.service";
import { showToast } from "@/core/utils/ui";
import type { OperationalAttachment, OperationalMessage, OperationalPhaseId } from "@shared/operational-ai/operational-ai.types";
import { OperationalDictationController } from "./chat.dictation";
import { OperationalMascotController } from "./chat.mascot";
import "./chat.css";
import "./composer.css";
import "./mascot.css";

export interface OperationalChatSendResult {
  snapshot: OperationalWorkspaceSnapshot;
  responded: boolean;
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[character] ?? character);
}

function formatMessageDateTime(createdAt: string): string {
  if (!createdAt) return "";
  const date = new Date(createdAt);
  if (isNaN(date.getTime())) return "";

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "p.m." : "a.m.";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const timeStr = `${hours}:${minutes} ${ampm}`;

  const today = new Date();
  if (date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear()) {
    return timeStr;
  }

  const day = date.getDate();
  const months = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
  const month = months[date.getMonth()];
  return `${day} de ${month}, ${timeStr}`;
}

function isDisplayableImage(attachment: OperationalAttachment): boolean {
  return attachment.mimeType !== "application/pdf" && !!attachment.downloadUrl;
}

export class OperationalChatController {
  pendingFiles: File[] = [];
  draftText = "";
  editingMessageId: string | null = null;
  private readonly previewUrls = new Map<File, string>();
  readonly dictation: OperationalDictationController;
  readonly mascot: OperationalMascotController;

  constructor(
    private readonly container: HTMLElement,
    private readonly isProcessing: () => boolean,
  ) {
    this.dictation = new OperationalDictationController(container, () => this.updateSendButtonState());
    this.mascot = new OperationalMascotController(container, isProcessing);
  }

  reset(): void {
    this.releaseAllPreviewUrls();
    this.pendingFiles = [];
    this.draftText = "";
    this.editingMessageId = null;
    this.mascot.reset();
  }

  destroy(): void {
    this.dictation.cancel();
    this.mascot.destroy();
    this.releaseAllPreviewUrls();
  }

  attachInteractions(): void {
    this.mascot.attachInteractions();
  }

  /** Adjuntos ficticios con vista previa local para que el mensaje optimista muestre la imagen de inmediato al enviar. */
  buildOptimisticAttachments(files: File[]): OperationalAttachment[] {
    return files.map((file, index) => ({
      id: `local_${Date.now()}_${index}`,
      name: file.name,
      mimeType: file.type as OperationalAttachment["mimeType"],
      size: file.size,
      storagePath: "",
      downloadUrl: this.previewUrlFor(file),
    }));
  }

  renderPane(messages: OperationalMessage[], emptyMessage = ""): string {
    const latestOwnerMessage = [...messages].reverse().find((m) => m.role === "dueno");
    const renderedMessages = messages.map((message) => {
      const isEditable = !this.isProcessing() && !!latestOwnerMessage && message.id === latestOwnerMessage.id;
      return this.renderMessage(message, isEditable);
    }).join("");
    const rendered = renderedMessages || (emptyMessage
      ? `<article class="flex justify-start"><div class="max-w-[88%] px-4 py-3 text-sm leading-relaxed opai-msg-assistant"><p class="whitespace-pre-wrap">${escapeHtml(emptyMessage)}</p></div></article>`
      : "");
    return `<div id="opai-messages" class="min-h-0 flex-1 space-y-4 overflow-y-auto p-5 opai-custom-scrollbar" aria-live="polite">${rendered}${this.isProcessing() ? `<div class="opai-thinking-row" role="status">${this.mascot.render("message", "thinking")}<span><i class="ph ph-circle-notch animate-spin text-lg" aria-hidden="true"></i>Analizando y organizando la información…</span></div>` : ""}</div>

      <div class="opai-input-wrapper relative isolate mx-4 mb-4 grid shrink-0 transform-gpu rounded-[1.75rem] p-2.5 shadow-2xl ${this.pendingFiles.length ? "has-attachments" : ""}">
          <div id="opai-attachment-previews-wrapper" class="contents">
            ${this.renderPendingAttachments()}
          </div>
          <input id="opai-file-input" type="file" multiple accept="image/png,image/jpeg,image/webp,application/pdf" class="hidden" />
          <button data-action="attach" class="opai-compose-action opai-compose-attach shrink-0" title="Adjuntar archivos" aria-label="Adjuntar archivos">
            <i class="ph ph-plus text-lg"></i>
          </button>

          <div class="opai-compose-field relative z-10 min-w-0 self-center">
            <textarea id="opai-message-input" maxlength="12000" rows="1" spellcheck="false" autocorrect="off" autocapitalize="sentences" class="w-full max-h-36 min-h-8 bg-transparent !border-none !border-0 text-sm outline-none focus:outline-none focus:ring-0 focus:!ring-0 !shadow-none opai-input-textarea resize-none py-1.5 placeholder-white/40" placeholder="Pregunta a Quetzalito">${escapeHtml(this.draftText)}</textarea>
            <div id="opai-recording-panel" class="hidden min-w-0 items-center gap-3" role="status" aria-live="polite">
              <span id="opai-recording-timer" class="opai-recording-timer">00:00</span>
              <canvas id="opai-live-waveform" class="opai-live-waveform min-w-0 flex-1" aria-label="Nivel de voz en tiempo real"></canvas>
              <span id="opai-transcribing-label" class="opai-transcribing-label hidden min-w-0 flex-1 items-center gap-2">
                <i class="ph ph-circle-notch animate-spin text-sm" aria-hidden="true"></i>
                <span>Generando texto…</span>
              </span>
            </div>
          </div>

          <div class="opai-compose-actions relative z-10 flex shrink-0 items-center gap-1.5">
            <button id="opai-dictate-btn" data-action="dictate" class="opai-compose-action opai-dictate-btn" title="Dictar con voz" aria-label="Iniciar dictado por voz" aria-pressed="false">
              <i class="ph ph-microphone text-lg" id="opai-mic-icon"></i>
            </button>
            <button data-action="send" id="opai-send-btn" class="opai-send-action hidden items-center justify-center" aria-label="Enviar">
              <i class="ph-bold ph-arrow-up text-lg"></i>
            </button>
          </div>
      </div>`;
  }

  private renderMessage(message: OperationalMessage, isEditable: boolean): string {
    const owner = message.role === "dueno";
    const pending = message.state === "pendiente_reintento";
    const isEditing = this.editingMessageId === message.id;
    const bubbleClass = owner ? "opai-msg-owner" : pending ? "opai-msg-pending" : "opai-msg-assistant";

    if (isEditing) {
      return `<article class="flex ${owner ? "justify-end" : "justify-start"}">
        <div class="relative w-full max-w-[88%] px-4 py-3 text-sm leading-relaxed ${bubbleClass}">
          <textarea data-edit-msg-input="${message.id}" class="w-full bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-white/50 resize-y min-h-[60px]" rows="2">${escapeHtml(message.text)}</textarea>
          <div class="mt-2 flex justify-end gap-2">
            <button data-action="cancel-edit-msg" class="rounded-lg px-2.5 py-1.5 text-[10px] font-black uppercase text-white/60 hover:text-white transition">Cancelar</button>
            <button data-action="save-edit-msg" data-msg-id="${message.id}" class="rounded-lg bg-white/20 px-3 py-1.5 text-[10px] font-black uppercase text-white hover:bg-white/30 transition">Guardar</button>
          </div>
        </div>
      </article>`;
    }

    const images = message.attachments.filter(isDisplayableImage);
    const files = message.attachments.filter((item) => !isDisplayableImage(item));
    const hasText = message.text.trim().length > 0;

    return `<article class="flex ${owner ? "justify-end" : "justify-start"}">
      <div class="group flex flex-col ${owner ? "items-end" : "items-start"} max-w-[88%] gap-1.5">
        ${images.length ? this.renderImageBubble(message.id, images, owner) : ""}
        ${hasText || files.length ? `<div class="relative px-4 py-3 text-sm leading-relaxed ${bubbleClass}">
          ${hasText ? `<p class="whitespace-pre-wrap">${escapeHtml(message.text)}</p>` : ""}
          ${files.length ? `<div class="${hasText ? "mt-2" : ""} flex flex-wrap gap-1">${files.map((item) => `<span class="rounded-md ${owner ? "bg-white/20 text-white" : "bg-white/10 text-white"} px-2 py-1 text-[9px] font-bold"><i class="ph ${item.mimeType === "application/pdf" ? "ph-file-pdf" : "ph-image"} mr-1" aria-hidden="true"></i>${escapeHtml(item.name)}</span>`).join("")}</div>` : ""}
          ${message.provider ? `<p class="mt-2 text-[9px] font-bold uppercase tracking-widest text-white/50">Procesado con ${message.provider === "deepseek" ? "DeepSeek" : "Gemini"}</p>` : ""}
        </div>` : ""}
        <div class="mt-1.5 flex items-center gap-2 px-1 text-[10px] text-white/50 select-none opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-auto">
          <span>${formatMessageDateTime(message.createdAt)}</span>
          <button data-action="copy-msg" data-msg-id="${message.id}" class="hover:text-white transition" title="Copiar texto"><i class="ph ph-copy text-xs"></i></button>
          ${isEditable ? `<button data-action="edit-msg-trigger" data-msg-id="${message.id}" class="hover:text-white transition" title="Editar mensaje"><i class="ph ph-pencil text-xs"></i></button>` : ""}
        </div>
      </div>
    </article>`;
  }

  private renderImageBubble(messageId: string, images: OperationalAttachment[], owner: boolean): string {
    const multi = images.length > 1;
    const trackId = `opai-img-track-${messageId}`;
    return `<div class="opai-image-bubble ${owner ? "justify-end" : "justify-start"}">
      ${multi ? `<button type="button" data-action="img-prev" data-track="${trackId}" class="opai-image-nav opai-image-nav--prev" aria-label="Imagen anterior"><i class="ph-bold ph-caret-left"></i></button>` : ""}
      <div id="${trackId}" class="opai-image-track">${images.map((image) => `<img src="${image.downloadUrl}" alt="${escapeHtml(image.name)}" class="opai-image-item" loading="lazy" />`).join("")}</div>
      ${multi ? `<button type="button" data-action="img-next" data-track="${trackId}" class="opai-image-nav opai-image-nav--next" aria-label="Imagen siguiente"><i class="ph-bold ph-caret-right"></i></button>` : ""}
    </div>`;
  }

  private renderPendingAttachments(): string {
    if (this.pendingFiles.length === 0) return "";
    return `<div id="opai-attachment-previews" class="opai-attachment-previews opai-custom-scrollbar" aria-label="Archivos listos para enviar">
      ${this.pendingFiles.map((file, index) => {
        const previewUrl = this.previewUrlFor(file);
        const isPdf = file.type === "application/pdf";
        const preview = isPdf
          ? `<object class="opai-attachment-pdf" data="${previewUrl}#page=1&toolbar=0&navpanes=0&scrollbar=0&view=FitH" type="application/pdf" aria-label="Primera página de ${escapeHtml(file.name)}"><span class="opai-attachment-fallback"><i class="ph-fill ph-file-pdf"></i>PDF</span></object>`
          : `<img class="opai-attachment-image" src="${previewUrl}" alt="Vista previa de ${escapeHtml(file.name)}" />`;
        return `<article class="opai-attachment-card">
          <div class="opai-attachment-visual">${preview}</div>
          <div class="opai-attachment-meta">
            <i class="ph ${isPdf ? "ph-file-pdf" : "ph-image"}" aria-hidden="true"></i>
            <span title="${escapeHtml(file.name)}">${escapeHtml(file.name)}</span>
          </div>
          <button data-action="remove-file" data-index="${index}" aria-label="Quitar ${escapeHtml(file.name)}" class="opai-attachment-remove"><i class="ph-bold ph-x"></i></button>
        </article>`;
      }).join("")}
    </div>`;
  }

  /** No requiere reenganchar listeners: los clics se delegan a nivel de contenedor en el orquestador. */
  refreshAttachmentPreviews(): void {
    const wrapper = this.container.querySelector<HTMLElement>("#opai-attachment-previews-wrapper");
    if (wrapper) wrapper.innerHTML = this.renderPendingAttachments();
    this.container.querySelector<HTMLElement>(".opai-input-wrapper")?.classList.toggle("has-attachments", this.pendingFiles.length > 0);
    this.updateSendButtonState();
  }

  scrollImageTrack(trackId: string, direction: 1 | -1): void {
    const track = this.container.querySelector<HTMLElement>(`#${CSS.escape(trackId)}`);
    if (!track) return;
    track.scrollBy({ left: direction * track.clientWidth, behavior: "smooth" });
  }

  previewUrlFor(file: File): string {
    const existing = this.previewUrls.get(file);
    if (existing) return existing;
    const previewUrl = URL.createObjectURL(file);
    this.previewUrls.set(file, previewUrl);
    return previewUrl;
  }

  releasePreviewUrl(file: File): void {
    const previewUrl = this.previewUrls.get(file);
    if (!previewUrl) return;
    URL.revokeObjectURL(previewUrl);
    this.previewUrls.delete(file);
  }

  releaseAllPreviewUrls(): void {
    this.previewUrls.forEach((previewUrl) => URL.revokeObjectURL(previewUrl));
    this.previewUrls.clear();
  }

  captureComposerDraft(): void {
    const input = this.container.querySelector<HTMLTextAreaElement>("#opai-message-input");
    if (input) this.draftText = input.value;
  }

  scrollMessages(): void {
    const element = this.container.querySelector<HTMLElement>("#opai-messages");
    if (element) element.scrollTop = element.scrollHeight;
  }

  updateSendButtonState(): void {
    const input = this.container.querySelector<HTMLTextAreaElement>("#opai-message-input");
    const sendBtn = this.container.querySelector<HTMLButtonElement>("#opai-send-btn");
    if (!sendBtn || !input) return;
    const hasContent = input.value.trim().length > 0 || this.pendingFiles.length > 0;
    sendBtn.disabled = !hasContent || this.isProcessing() || this.dictation.busy;
    if (hasContent && !this.isProcessing() && !this.dictation.busy) {
      sendBtn.classList.remove("hidden");
      sendBtn.classList.add("flex");
    } else {
      sendBtn.classList.add("hidden");
      sendBtn.classList.remove("flex");
    }
  }

  /** Envía texto + adjuntos. `renderNow` pinta el mensaje optimista (con las previews locales) antes de esperar la red. */
  async sendMessage(snapshot: OperationalWorkspaceSnapshot, currentPhase: OperationalPhaseId, input: HTMLTextAreaElement | null, renderNow: () => void): Promise<OperationalChatSendResult> {
    const text = input?.value.trim() ?? "";
    const requestId = newOperationalRequestId();
    const filesToSend = [...this.pendingFiles];
    const optimisticAttachments = this.buildOptimisticAttachments(filesToSend);
    this.pendingFiles = [];
    snapshot.messages.push({ id: `${requestId}_owner`, role: "dueno", text, state: "completado", attachments: optimisticAttachments, createdAt: new Date().toISOString() });
    this.draftText = "";
    if (input) { input.value = ""; input.style.height = "auto"; }
    renderNow();
    try {
      const attachments = await uploadOperationalAttachments(snapshot.profile.id, requestId, filesToSend);
      await processOperationalMessage({ requestId, workspaceId: snapshot.profile.id, message: text, attachments, currentPhase });
      const updated = await loadOperationalWorkspace(snapshot.profile.id);
      filesToSend.forEach((file) => this.releasePreviewUrl(file));
      return { snapshot: updated, responded: true };
    } catch (error) {
      showToast(error instanceof Error ? error.message : "No se pudo procesar el mensaje.", "error");
      const updated = await loadOperationalWorkspace(snapshot.profile.id).catch(() => snapshot);
      const messageWasSaved = updated.messages.some((message) => message.id === `${requestId}_owner`);
      if (!messageWasSaved) {
        this.pendingFiles = filesToSend;
        if (!this.draftText.trim()) this.draftText = text;
      }
      return { snapshot: updated, responded: false };
    }
  }

  /** Reemplaza un mensaje ya enviado (y todo lo posterior) por su versión editada, y vuelve a procesarlo. */
  async saveEditedMessage(snapshot: OperationalWorkspaceSnapshot, currentPhase: OperationalPhaseId, messageId: string, newText: string, renderNow: () => void): Promise<OperationalChatSendResult | null> {
    const msgIndex = snapshot.messages.findIndex((m) => m.id === messageId);
    if (msgIndex === -1) return null;
    const originalMsg = snapshot.messages[msgIndex];
    this.editingMessageId = null;
    if (originalMsg.text === newText) return null;

    const messagesToDelete = snapshot.messages.slice(msgIndex).map((m) => m.id);
    const attachments = originalMsg.attachments || [];
    const requestId = newOperationalRequestId();
    const optimisticUserMsg: OperationalMessage = { id: `${requestId}_owner`, role: "dueno", text: newText, state: "completado", attachments, createdAt: new Date().toISOString() };

    const originalMessages = [...snapshot.messages];
    snapshot.messages = snapshot.messages.slice(0, msgIndex);
    snapshot.messages.push(optimisticUserMsg);
    renderNow();

    try {
      await deleteOperationalMessages(snapshot.profile.id, messagesToDelete);
      await processOperationalMessage({ requestId, workspaceId: snapshot.profile.id, message: newText, attachments, currentPhase });
      const updated = await loadOperationalWorkspace(snapshot.profile.id);
      return { snapshot: updated, responded: true };
    } catch (error) {
      showToast(error instanceof Error ? error.message : "No se pudo editar el mensaje.", "error");
      snapshot.messages = originalMessages;
      const updated = await loadOperationalWorkspace(snapshot.profile.id).catch(() => snapshot);
      return { snapshot: updated, responded: false };
    }
  }
}
